import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Heart, HandHeart, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CafLogo } from "@/components/CafLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useSite } from "@/lib/site";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
];

const PROGRAMS = [
  { to: "/programs", label: "All Programs" },
  { to: "/programs/disaster-relief", label: "Disaster Relief" },
  { to: "/programs/education-support", label: "Education" },
  { to: "/programs/medical-assistance", label: "Medical" },
  { to: "/programs/environmental-protection", label: "Environment" },
  { to: "/programs/womens-empowerment", label: "Women's Empowerment" },
  { to: "/programs/youth-leadership", label: "Youth Leadership" },
];

const AWARENESS = [
  { to: "/awareness", label: "Awareness Hub" },
  { to: "/awareness/know-your-rights", label: "Know Your Rights" },
  { to: "/awareness/consumer", label: "Consumer Awareness" },
  { to: "/awareness/legal", label: "Legal Awareness" },
  { to: "/awareness/digital-safety", label: "Digital Safety" },
  { to: "/awareness/environmental", label: "Environmental Awareness" },
];

const ENGAGE = [
  { to: "/volunteer", label: "Volunteer" },
  { to: "/membership", label: "Digital Membership" },
  { to: "/training", label: "Training Resources" },
  { to: "/volunteer-stories", label: "Volunteer Stories" },
  { to: "/youth-voice", label: "Youth Voice" },
  { to: "/report-issue", label: "Report Issue" },
];

const MEDIA = [
  { to: "/media", label: "Media Centre" },
  { to: "/press-releases", label: "Press Releases" },
  { to: "/events", label: "Events" },
  { to: "/blog", label: "News & Blog" },
  { to: "/video-library", label: "Video Library" },
];

const TRUST = [
  { to: "/transparency", label: "Transparency Dashboard" },
  { to: "/governance", label: "Governance" },
  { to: "/annual-reports", label: "Annual Reports" },
  { to: "/csr", label: "CSR Partnerships" },
  { to: "/grievance-redressal", label: "Grievance Redressal" },
];

const MegaItem = ({ label, items }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid={`nav-mega-${label.toLowerCase()}`} className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md text-foreground/85 hover:text-foreground hover:bg-[hsl(var(--surface-tint))]">
        {label} <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="min-w-[16rem] p-2">
      {items.map((i) => (
        <Link key={i.to} to={i.to} data-testid={`nav-mega-item-${i.to}`} className="block px-3 py-2 rounded-md text-sm text-foreground hover:bg-[hsl(var(--surface-tint))]">{i.label}</Link>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const site = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      {/* Top tricolour + slogan strip */}
      <div className="hidden md:block relative overflow-hidden bg-black text-white">
        <div className="absolute inset-x-0 top-0 h-0.5 tricolour-strip-h" aria-hidden="true" />
        <div className="container-wide flex items-center justify-between h-8 text-[11px] tracking-[0.15em] uppercase">
          <div className="text-white/80">{site.tagline ? site.tagline.toUpperCase() : "Aware Today • Responsible Tomorrow • Stronger Together"}</div>
          <div className="flex items-center gap-4 text-white/70">
            <a data-testid="topbar-phone" href={`tel:${(site.contact_phone || "").replace(/\s/g, "")}`} className="hover:text-[hsl(var(--orange))]">{site.contact_phone}</a>
            <a data-testid="topbar-email" href={`mailto:${site.contact_email}`} className="hover:text-[hsl(var(--orange))]">{site.contact_email}</a>
            <span className="text-[hsl(var(--orange))]">12A • 80G • CSR-1</span>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 w-full transition-all duration-200 ${
          scrolled ? "glass border-b border-[hsl(var(--border))]" : "bg-background/70 backdrop-blur"
        }`}
      >
        <div className="container-wide flex items-center justify-between h-16">
          <Link to="/" className="focus-ring rounded-md" aria-label="CAF Home"><CafLogo /></Link>
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary">
            {PRIMARY.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-foreground bg-[hsl(var(--surface-tint))]" : "text-foreground/85 hover:text-foreground hover:bg-[hsl(var(--surface-tint))]"}`}>
                {n.label}
              </NavLink>
            ))}
            <MegaItem label="Programs" items={PROGRAMS} />
            <MegaItem label="Awareness" items={AWARENESS} />
            <MegaItem label="Engage" items={ENGAGE} />
            <MegaItem label="Media" items={MEDIA} />
            <MegaItem label="Trust" items={TRUST} />
            <NavLink to="/contact" data-testid="nav-link-contact" className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-md ${isActive ? "text-foreground bg-[hsl(var(--surface-tint))]" : "text-foreground/85 hover:text-foreground hover:bg-[hsl(var(--surface-tint))]"}`}>Contact</NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/volunteer" className="hidden md:inline-flex">
              <Button variant="outline" data-testid="header-volunteer-button" className="rounded-full border-foreground text-foreground hover:bg-foreground hover:text-background">
                <HandHeart className="h-4 w-4 mr-1.5" /> Volunteer
              </Button>
            </Link>
            <Link to="/donate">
              <Button data-testid="header-donate-button" className="rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold">
                <Heart className="h-4 w-4 mr-1.5" /> Donate
              </Button>
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="header-mobile-menu-button" className="xl:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-border text-foreground focus-ring" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-background overflow-y-auto">
                <SheetHeader><SheetTitle className="text-left"><CafLogo /></SheetTitle></SheetHeader>
                <nav className="mt-6 flex flex-col gap-4" aria-label="Mobile">
                  {[["Home", PRIMARY], ["Programs", PROGRAMS], ["Awareness", AWARENESS], ["Engage", ENGAGE], ["Media", MEDIA], ["Trust", TRUST]].map(([section, items]) => (
                    <div key={section}>
                      <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-ink))] mb-1.5">{section}</div>
                      <div className="flex flex-col">
                        {items.map((n) => (
                          <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/[^a-z]/g,"-")}`}
                            className={({ isActive }) => `px-2 py-2 text-sm rounded-md font-medium ${isActive ? "bg-[hsl(var(--surface-tint))] text-foreground" : "text-foreground/90"}`}>
                            {n.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  ))}
                </nav>
                <div className="mt-6 grid grid-cols-2 gap-2">
                  <Link to="/volunteer"><Button variant="outline" className="w-full rounded-full border-foreground text-foreground"><HandHeart className="h-4 w-4 mr-1.5" />Volunteer</Button></Link>
                  <Link to="/donate"><Button className="w-full rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold"><Heart className="h-4 w-4 mr-1.5" />Donate</Button></Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};
